import { createSlice } from "@reduxjs/toolkit";
import * as tPromise from "io-ts-promise";
import {
  MailOrderSettings,
  MailOrderSource,
  MailOrderTemplateListItem,
  UpdateMailOrderSettings,
  mailOrderSettingsT,
  mailOrderSourceT,
  mailOrderTemplateDetailT,
  mailOrderTemplateListT,
  updateMailOrderSettingsT,
  updateMailOrderTemplateResultT,
} from "dora-contracts";
import axios, { getData, isAxiosError } from "../../../axios";
import { createErrorReportingAsyncThunk } from "../../helpers";
import { notifyL } from "../../notifications";
import {
  MailOrderTemplate,
  MailOrderTemplateDraft,
  mailOrderTemplateDraftT,
} from "./types";

const prefix = "data/mail-orders";

export type LoadStatus = "loading" | "loaded" | "error";

/**
 * Loading the source mail has three outcomes, and the card has to tell them
 * apart: `none` (the server answered 404 - this cargo was not created from a
 * mail, which is the normal case for a hand-entered order), `loaded`, and
 * `error` (anything else - the cargo may well have a source mail that we
 * failed to show, so the user is told and Sentry gets it).
 */
type MailSourceState =
  | { status: "loading" }
  | { status: "none" }
  | { status: "error" }
  | { status: "loaded"; mail: MailOrderSource };

/** Outcome of "save corrections to the mail mapping template" for one cargo. */
export type TemplateSaveState =
  | "saving"
  | "saved"
  | "multipleOrdersInMail"
  | "error";

type State = {
  settings: MailOrderSettings | null;
  settingsStatus: LoadStatus | null;
  // Kept while a settings save is in flight so the optimistic update can be
  // rolled back if the server rejects it.
  settingsBeforeSave: MailOrderSettings | null;
  source: Record<string, MailSourceState | undefined>;
  templateSave: Record<string, TemplateSaveState | undefined>;
  templates: MailOrderTemplateListItem[] | null;
  templatesStatus: LoadStatus | null;
  templateDetail: MailOrderTemplate | null;
  templateDetailStatus: LoadStatus | null;
  savingTemplateDetail: boolean;
};

const initialState: State = {
  settings: null,
  settingsStatus: null,
  settingsBeforeSave: null,
  source: {},
  templateSave: {},
  templates: null,
  templatesStatus: null,
  templateDetail: null,
  templateDetailStatus: null,
  savingTemplateDetail: false,
};

export const loadMailOrderSettings = createErrorReportingAsyncThunk(
  `${prefix}/load-settings`,
  async () =>
    axios
      .get("/api/mail-orders/settings")
      .then(getData)
      .then(tPromise.decode(mailOrderSettingsT))
);

export const updateMailOrderSettings = createErrorReportingAsyncThunk(
  `${prefix}/update-settings`,
  async (input: UpdateMailOrderSettings) =>
    axios
      .put("/api/mail-orders/settings", updateMailOrderSettingsT.encode(input))
      .then(getData)
      .then(tPromise.decode(mailOrderSettingsT))
);

/**
 * The mail a cargo was created from. A 404 is the server saying "this cargo
 * did not come from a mail" - an ordinary empty state, so it resolves to
 * `null`. Every other failure (5xx, a decode failure, ...) is rethrown and
 * reported through the thunk wrapper: Sentry plus the error toast.
 */
export const loadMailSource = createErrorReportingAsyncThunk(
  `${prefix}/load-mail-source`,
  async (cargoId: string) => {
    try {
      const data = await axios
        .get(`/api/mail-orders/cargo/${cargoId}`)
        .then(getData);
      return { cargoId, mail: await tPromise.decode(mailOrderSourceT)(data) };
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 404) {
        return { cargoId, mail: null };
      }
      throw e;
    }
  }
);

/**
 * Teach the mapping template for this sender+format from the cargo as the
 * dispatcher corrected it. `updated: false` means the source mail produced
 * several orders, so this single order is not a usable example for the format
 * - a normal outcome the card explains, not a failure.
 */
export const saveMailOrderTemplateFromCargo = createErrorReportingAsyncThunk(
  `${prefix}/save-template-from-cargo`,
  async (cargoId: string) => {
    const res = await axios
      .post(`/api/mail-orders/cargo/${cargoId}/update-template`)
      .then(getData)
      .then(tPromise.decode(updateMailOrderTemplateResultT));
    return { cargoId, updated: res.updated };
  }
);

export const loadMailOrderTemplates = createErrorReportingAsyncThunk(
  `${prefix}/load-templates`,
  async () =>
    axios
      .get("/api/mail-orders/templates")
      .then(getData)
      .then(tPromise.decode(mailOrderTemplateListT))
);

export const loadMailOrderTemplate = createErrorReportingAsyncThunk(
  `${prefix}/load-template`,
  async (mailOrderTemplateId: string): Promise<MailOrderTemplate> => {
    const detail = await axios
      .get(`/api/mail-orders/templates/${mailOrderTemplateId}`)
      .then(getData)
      .then(tPromise.decode(mailOrderTemplateDetailT));
    const exampleOutput = await tPromise.decode(mailOrderTemplateDraftT)(
      detail.exampleOutput
    );
    return { ...detail, exampleOutput };
  }
);

export const updateMailOrderTemplate = createErrorReportingAsyncThunk(
  `${prefix}/update-template`,
  async (
    input: {
      mailOrderTemplateId: string;
      exampleOutput: MailOrderTemplateDraft;
    },
    thunkAPI
  ) => {
    await axios.put(
      `/api/mail-orders/templates/${input.mailOrderTemplateId}`,
      { exampleOutput: mailOrderTemplateDraftT.encode(input.exampleOutput) }
    );
    thunkAPI.dispatch(
      notifyL({
        namespace: "settings",
        key: "mailOrders.templateSavedNotification",
        type: "success",
      })
    );
    // The list column shows "last changed", so it is stale after a save.
    thunkAPI.dispatch(loadMailOrderTemplates());
  }
);

export const deleteMailOrderTemplate = createErrorReportingAsyncThunk(
  `${prefix}/delete-template`,
  async (mailOrderTemplateId: string) => {
    await axios.delete(`/api/mail-orders/templates/${mailOrderTemplateId}`);
    return mailOrderTemplateId;
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    unloadMailSource: (state, action: { payload: string }) => {
      delete state.source[action.payload];
      delete state.templateSave[action.payload];
    },
    clearMailOrderTemplateDetail: (state) => {
      state.templateDetail = null;
      state.templateDetailStatus = null;
      state.savingTemplateDetail = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadMailOrderSettings.pending, (state) => {
      state.settingsStatus = "loading";
    });
    builder.addCase(loadMailOrderSettings.rejected, (state) => {
      state.settingsStatus = "error";
    });
    builder.addCase(loadMailOrderSettings.fulfilled, (state, action) => {
      state.settingsStatus = "loaded";
      state.settings = action.payload;
    });

    builder.addCase(updateMailOrderSettings.pending, (state, action) => {
      // Optimistic: a switch must not lag a round trip behind the click.
      state.settingsBeforeSave = state.settings;
      if (state.settings) {
        state.settings = { ...state.settings, ...action.meta.arg };
      }
    });
    builder.addCase(updateMailOrderSettings.rejected, (state) => {
      state.settings = state.settingsBeforeSave;
      state.settingsBeforeSave = null;
    });
    builder.addCase(updateMailOrderSettings.fulfilled, (state, action) => {
      state.settings = action.payload;
      state.settingsBeforeSave = null;
    });

    builder.addCase(loadMailSource.pending, (state, action) => {
      state.source[action.meta.arg] = { status: "loading" };
    });
    builder.addCase(loadMailSource.rejected, (state, action) => {
      state.source[action.meta.arg] = { status: "error" };
    });
    builder.addCase(loadMailSource.fulfilled, (state, action) => {
      const { cargoId, mail } = action.payload;
      state.source[cargoId] = mail
        ? { status: "loaded", mail }
        : { status: "none" };
    });

    builder.addCase(saveMailOrderTemplateFromCargo.pending, (state, action) => {
      state.templateSave[action.meta.arg] = "saving";
    });
    builder.addCase(
      saveMailOrderTemplateFromCargo.rejected,
      (state, action) => {
        state.templateSave[action.meta.arg] = "error";
      }
    );
    builder.addCase(
      saveMailOrderTemplateFromCargo.fulfilled,
      (state, action) => {
        state.templateSave[action.payload.cargoId] = action.payload.updated
          ? "saved"
          : "multipleOrdersInMail";
      }
    );

    builder.addCase(loadMailOrderTemplates.pending, (state) => {
      state.templatesStatus = "loading";
    });
    builder.addCase(loadMailOrderTemplates.rejected, (state) => {
      state.templatesStatus = "error";
    });
    builder.addCase(loadMailOrderTemplates.fulfilled, (state, action) => {
      state.templatesStatus = "loaded";
      state.templates = action.payload;
    });

    builder.addCase(loadMailOrderTemplate.pending, (state) => {
      state.templateDetailStatus = "loading";
      state.templateDetail = null;
    });
    builder.addCase(loadMailOrderTemplate.rejected, (state) => {
      state.templateDetailStatus = "error";
    });
    builder.addCase(loadMailOrderTemplate.fulfilled, (state, action) => {
      state.templateDetailStatus = "loaded";
      state.templateDetail = action.payload;
    });

    builder.addCase(updateMailOrderTemplate.pending, (state) => {
      state.savingTemplateDetail = true;
    });
    builder.addCase(updateMailOrderTemplate.rejected, (state) => {
      state.savingTemplateDetail = false;
    });
    builder.addCase(updateMailOrderTemplate.fulfilled, (state) => {
      state.savingTemplateDetail = false;
    });

    builder.addCase(deleteMailOrderTemplate.fulfilled, (state, action) => {
      state.templates =
        state.templates?.filter((x) => x.id !== action.payload) ?? null;
    });
  },
});

export default slice.reducer;

export const { unloadMailSource, clearMailOrderTemplateDetail } = slice.actions;
